'use client';

import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Memoria de navegación de los listados públicos.
 *
 * El problema que resuelve (reportado por Mallorca): el listado pagina con
 * "Ver más" y esa página vivía en un `useState` del componente. Al entrar a una
 * ficha el componente se desmonta, y al volver vuelve a renderizar sólo el
 * primer bloque: el documento pasa de ~16.800px a ~6.200px y el scroll que el
 * navegador intenta restaurar ya no existe. Safari lo recorta al máximo (el
 * usuario aparece en el footer) y Chrome lo manda a 0.
 *
 * Acá se guarda, por entrada del historial, la foto del momento en que el
 * usuario tocó una tarjeta: cuántos bloques llevaba abiertos y a qué altura
 * estaba.
 */
export interface ListPosition {
  /**
   * Cuánto llevaba abierto el listado con "Ver más". El significado lo pone
   * cada listado: en /vehicles es el número de página; en la grilla del home,
   * la cantidad de tarjetas montadas. Acá viaja opaco.
   */
  cursor: number;
  /** `window.scrollY` al momento de salir. Respaldo del ancla. */
  scrollY: number;
  /** Filtros/orden/búsqueda vigentes: si cambiaron, la posición ya no sirve. */
  signature: string;
  /**
   * Ancla real: qué ficha estaba arriba de todo y a qué altura de la pantalla.
   *
   * Un número de scroll solo no alcanza. En los homes del Builder hay secciones
   * ARRIBA de la grilla que terminan de cargar después que la lista; cuando lo
   * hacen, la lista entera baja y ese mismo scrollY ya apunta a otros autos.
   * Medido en Demo Argentina: restaurado a 5467, 750ms más tarde el contenido
   * de arriba creció 766px y el usuario quedó una pantalla más abajo.
   */
  anchorHref?: string;
  anchorTop?: number;
}

/** Tope de fotos guardadas; se descarta la más vieja. */
const MAX_POSICIONES = 20;

interface ListNavigationState {
  positions: Record<string, ListPosition>;
  remember: (token: string, position: ListPosition) => void;
  forget: (token: string) => void;
}

export const useListNavigationStore = create<ListNavigationState>()(
  persist(
    (set) => ({
      positions: {},

      remember: (token, position) =>
        set((state) => {
          const positions = { ...state.positions, [token]: position };
          // Una entrada por entrada del historial que visitó un listado. Son
          // ~120 bytes cada una y en una visita real no pasan de un puñado,
          // pero el tope evita que una sesión muy larga llene sessionStorage.
          const claves = Object.keys(positions);
          if (claves.length > MAX_POSICIONES) {
            delete positions[claves[0]];
          }
          return { positions };
        }),

      forget: (token) =>
        set((state) => {
          if (!(token in state.positions)) return state;
          const positions = { ...state.positions };
          delete positions[token];
          return { positions };
        }),
    }),
    {
      name: 'goauto-list-nav',
      // sessionStorage y no localStorage: la posición pertenece a ESTA visita.
      // Volver mañana al sitio tiene que abrir el listado arriba de todo.
      // En el server `sessionStorage` no existe; createJSONStorage atrapa el
      // throw y deja el store sin persistencia, que es lo correcto en SSR.
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);

/**
 * Campo propio dentro de `history.state`, que es por ENTRADA del historial.
 *
 * Primero esto se resolvió con una marca de `popstate`, y no sirve: medido en
 * el navegador, Next vuelve a renderizar el listado ANTES de despachar el
 * `popstate`, así que al decidir con cuántos vehículos arrancar la marca
 * todavía no existía. `history.state`, en cambio, ya está restaurado cuando
 * corre el render, y además distingue solo lo que hay que distinguir: volver a
 * ESTA entrada del historial restaura; entrar al listado desde el menú crea una
 * entrada nueva, sin token, y abre arriba de todo.
 */
const TOKEN_FIELD = 'goautoListToken';

/**
 * Marca que pone `useCerrarConAtras` en la entrada que empuja al abrir una
 * capa (visor de fotos, menú móvil, paneles de filtros y orden). Mientras esa
 * entrada esté arriba, la memoria del listado no debe escribir ni marcar nada:
 * la posición pertenece a la entrada del listado que quedó debajo, y escribir
 * acá la dejaría colgada de una entrada que desaparece al cerrar la capa.
 *
 * El nombre del campo lo define ese hook; si cambia allá, hay que cambiarlo acá.
 */
const OVERLAY_FIELD = 'cerrarConAtras';

/** ¿La entrada actual del historial es la de una capa abierta? */
export function isOverlayHistoryEntry(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return (window.history.state as Record<string, unknown> | null)?.[OVERLAY_FIELD] === true;
  } catch {
    return false;
  }
}

/** Marca la entrada actual del historial y devuelve su token. */
export function tagCurrentHistoryEntry(scope: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const current = window.history.state as Record<string, unknown> | null;
    const existing = current?.[TOKEN_FIELD];
    const token =
      typeof existing === 'string' && existing.startsWith(`${scope}:`)
        ? existing
        : `${scope}:${Date.now().toString(36)}${Math.random()
            .toString(36)
            .slice(2, 8)}`;
    // Se conserva el resto del state: ahí viven las claves del router de Next.
    window.history.replaceState({ ...(current || {}), [TOKEN_FIELD]: token }, '');
    return token;
  } catch {
    return null;
  }
}

/** Token de la entrada del historial en la que estamos parados, si tiene. */
export function currentHistoryToken(scope: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = (window.history.state as Record<string, unknown> | null)?.[
      TOKEN_FIELD
    ];
    return typeof value === 'string' && value.startsWith(`${scope}:`)
      ? value
      : null;
  } catch {
    return null;
  }
}

export default useListNavigationStore;
